<template>
  <iRow
    class="story-editor-component-wrapper"
    :style-overrides="{
      paddingLeft: resolvedPaddingLeft,
      paddingRight: resolvedPaddingRight
    }"
    align="center"
    :width="width"
    :height="height"
    :min-height="minHeight"
    :max-width="maxWidth"
    :max-height="maxHeight"
    :min-width="minWidth"
  >
    <iEditor
      ref="editor"
      :key="playbackVersion"
      :branding-styles="getCssMappedConfiguration"
      :generate-link-ranges-with-tracking-urls="generateLinkRangesWithTrackingUrls"
      :generate-product-collection="generateProductCollectionFromAi"
      :generate-text="generateStoryTextContent"
      :generate-text-changes="generateStoryTextContentChanges"
      :max-width="maxWidth"
      :min-height="minHeight"
      :on-add-audio-metadata="handleAddAudioMetadata"
      :preview="preview"
      :search-products="searchProducts"
      :segments="resolvedSegments"
      :upload-post-audio="uploadAudioFunction"
      :upload-post-image="uploadImageFunction"
      :upload-post-video="uploadVideoFunction"
      :pick-media="openMediaPicker"
      height="auto"
      include-margin
      :use-border="useBorder"
      :hide-segment-controls="hideSegmentControls"
      variant="builder"
      width="fill"
      :font-color="fontColor"
      :font-size="fontSize"
      :font-weight="fontWeight"
      :style-overrides="editorStyleOverrides"
      @input="saveWithDebounce"
      @handle-link-modal-open="handleLinkModalOpen"
    >
      <template #above-editor />
    </iEditor>
    <AudioCoverImageModal
      :current-cover-image="selectedAudioSegmentData?.coverImage"
      :upload-function="uploadImageFunction"
      :visible="showAudioMetadataModal"
      @close="closeAudioMetadataModal"
      @save="handleAudioCoverImageSave"
    />
    <MediaPickerModal
      :visible="mediaPickerVisible"
      :media-type="mediaPickerType"
      :theme-id="0"
      @select="handleMediaPickerSelect"
      @close="handleMediaPickerClose"
    />
    <teleport to="body">
      <RichTextDestinationPickerModal
        :link-data="linkData"
        :original-link-value="originalLinkData.linkValue"
        :publication-id="publicationId"
        :rich-text-ref="linkData.ref"
        :show="showLinkModal"
        @close="handleLinkModalClose"
        @update="linkData = {...linkData, ...$event}"
      />
    </teleport>
  </iRow>
</template>

<script>
import { mapActions, mapState } from "pinia";
import useBrandingStore from "%/dynamic-component-editor/stores/branding-store";
import { storyEditorStyleOverrides } from "%/dynamic-component-editor/helpers/story-editor-style-overrides";
import { useStoryStore } from "%/dynamic-component-editor/stores/story-store";
import AudioCoverImageModal from "@/components/publication/AudioCoverImageModal.vue";
import MediaPickerModal from "%/components/MediaPickerModal.vue";
import RichTextDestinationPickerModal from "%/dynamic-component-editor/components/RichTextDestinationPickerModal.vue";
import { useAffiliateLinkStore } from "%/dynamic-component-editor/stores/affiliate-link-store";
import { StoryVideoPlaybackMixin } from "%/mixins/story-video-playback-mixin";
import { Enum } from "@vcl";

export default {
  name: "PlainStoryEditor",
  components: { RichTextDestinationPickerModal, AudioCoverImageModal, MediaPickerModal },
  mixins: [StoryVideoPlaybackMixin],
  props: {
    paddingLeft: {
      type: String,
      default: "",
      required: false,
    },
    paddingRight: {
      type: String,
      default: "",
      required: false,
    },
    modelValue: {
      type: Array,
      default: () => [],
      required: false,
    },
    publicationId: {
      type: [Number],
      required: true,
    },
    preview: {
      type: Boolean,
      default: false,
      required: false,
    },
    useBorder: {
      type: Boolean,
      default: true,
      required: false,
    },
    width: {
      type: [Number, Enum],
      required: false,
      default: "fill",
      options: ["fill", "hug"],
    },
    borderRadius: {
      type: [Number, String],
      default: "standard",
      required: false,
    },
    backgroundColor: {
      type: String,
      default: null,
      required: false,
    },
    hideSegmentControls: {
      type: Boolean,
      default: false,
      required: false,
    },
    height: {
      type: [Number, Enum],
      required: false,
      default: "hug",
      options: ["fill", "hug", "auto"],
    },
    maxWidth: {
      type: [Number, Enum],
      required: false,
      default: "unset",
      options: ["unset"],
    },
    minWidth: {
      type: [Number, Enum],
      required: false,
      default: "unset",
      options: ["unset"],
    },
    maxHeight: {
      type: [Number, Enum],
      required: false,
      default: 1200,
      options: ["unset"],
    },
    minHeight: {
      type: [Number, Enum],
      required: false,
      default: 100,
      options: ["unset"],
    },
    fontColor: {
      type: String,
      default: "",
      required: false,
    },
    fontSize: {
      type: [String, Number],
      default: "",
      required: false,
    },
    fontWeight: {
      type: String,
      default: "",
      required: false,
    },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      showLinkModal: false,
      linkData: {},
      originalLinkData: {},
      showAudioMetadataModal: false,
      selectedAudioSegmentId: null,
      selectedAudioSegmentData: null,
      debounceTimeout: null,
      mediaPickerVisible: false,
      mediaPickerType: "video",
      pickMediaResolve: null,
    };
  },
  computed: {
    resolvedPaddingLeft() {
      return this.paddingLeft || (this.preview ? "none" : "30px");
    },
    resolvedPaddingRight() {
      return this.paddingRight || (this.preview ? "none" : "30px");
    },
    ...mapState(useBrandingStore, [
      "getCssMappedConfiguration",
    ]),
    editorStyleOverrides() {
      return storyEditorStyleOverrides({
        borderRadius: this.borderRadius,
        backgroundColor: this.backgroundColor,
      });
    },
    value() {
      if (Array.isArray(this.modelValue)) {
        return this.modelValue;
      }
      return [];
    },
  },
  methods: {
    ...mapActions(useStoryStore, [
      "uploadStoryImage",
      "uploadStoryVideo",
      "uploadStoryAudio",
    ]),
    ...mapActions(useAffiliateLinkStore, [
      "searchAffiliateLinkProducts",
      "createLink",
    ]),
    uploadImageFunction(file) {
      return this.uploadStoryImage(file);
    },
    uploadVideoFunction(file) {
      return this.uploadStoryVideo(file);
    },
    uploadAudioFunction(file) {
      return this.uploadStoryAudio(file);
    },
    handleAddAudioMetadata({ segmentId, data }) {
      this.selectedAudioSegmentId = segmentId;
      this.selectedAudioSegmentData = data;
      this.showAudioMetadataModal = true;
    },
    handleAudioCoverImageSave(coverImageUrl) {
      if (this.selectedAudioSegmentId) {
        this.$refs.editor.updateSegmentData(this.selectedAudioSegmentId, {
          coverImage: coverImageUrl,
        });
      }
      this.closeAudioMetadataModal();
    },
    closeAudioMetadataModal() {
      this.showAudioMetadataModal = false;
      this.selectedAudioSegmentId = null;
      this.selectedAudioSegmentData = null;
    },
    openMediaPicker(mediaType) {
      return new Promise(resolve => {
        this.mediaPickerType = mediaType;
        this.mediaPickerVisible = true;
        this.pickMediaResolve = resolve;
      });
    },
    handleMediaPickerSelect(media) {
      this.mediaPickerVisible = false;
      if (!this.pickMediaResolve) {
        return;
      }
      this.pickMediaResolve(media && media.media_id ? { mediaId: media.media_id } : null);
      this.pickMediaResolve = null;
    },
    handleMediaPickerClose() {
      this.mediaPickerVisible = false;
      if (!this.pickMediaResolve) {
        return;
      }
      this.pickMediaResolve(null);
      this.pickMediaResolve = null;
    },
    async searchProducts(query) {
      return await this.searchAffiliateLinkProducts(query);
    },
    async generateStoryTextContent(prompt, successCallback) {
      return await useStoryStore().generateTextStoryContent(prompt, successCallback);
    },
    async generateProductCollectionFromAi(prompt, successCallback, errorCallback) {
      return await useStoryStore().generateAiProductCollection(prompt, successCallback, errorCallback);
    },
    async generateStoryTextContentChanges(prompt, content, successCallback) {
      return await useStoryStore().generateTextStoryContentChanges(prompt, content, successCallback);
    },
    handleLinkModalClose() {
      this.linkData = {};
      this.originalLinkData = {};
      this.showLinkModal = false;
    },
    async generateLinkRangesWithTrackingUrls(ranges, callback) {
      const promises = ranges.map(range => {
        return new Promise(resolve => {
          this.createLink(
            range.url,
            result => {
              if (result.short_link) {
                resolve({
                  ...range,
                  trackingUrl: result.short_link,
                  shortLinkData: result,
                });
              } else {
                resolve(range);
              }
            },
            () => {
              resolve(range);
            },
          );
        });
      });

      const results = await Promise.all(promises);
      callback(results);
    },
    saveWithDebounce(value) {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.debounceTimeout = setTimeout(() => {
        this.$emit("update:modelValue", this.stripResolvedVideoSrc(value.segments.filter(segment => {
          return !!segment.segment_content;
        })).map(segment => {
          segment.segment_content = JSON.stringify(segment.segment_content);
          return segment;
        }));
      }, 100);
    },
    handleLinkModalOpen(event) {
      this.linkData = event;
      this.originalLinkData = event;
      this.showLinkModal = true;
      event.openModal(true);
    },
  },
};
</script>

<style lang="scss" scoped>

</style>
